<!-- StrokeMixChart — donut + dominant callout + legend.
     Extracted from P5StrokeDonut; lap-list and proto chrome stripped.
     Legend text raised to 15px (P5 used ~12px). -->
<script lang="ts">
  import { onMount } from "svelte";
  import { mountChart, darkTooltip } from "../../lib/echartsHelpers";
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { strokeColor, COLORS } from "../../lib/designTokens";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  let donutEl: HTMLDivElement;

  onMount(() => mountChart(donutEl, (chart) => chart.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      ...darkTooltip((p: { name: string; value: number; percent: number }) =>
        `<strong>${p.name}</strong><br/>${p.value.toFixed(0)} m · ${p.percent.toFixed(1)}%`),
    },
    series: [
      {
        type: "pie",
        radius: ["55%", "85%"],
        minAngle: 6,
        avoidLabelOverlap: false,
        itemStyle: { borderColor: COLORS.navyMid, borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        data: dw.strokeBreakdown.map((s) => ({
          name: s.style,
          value: s.meters,
          itemStyle: { color: strokeColor(s.style) },
        })),
      },
    ],
  })));

  let dominantStroke = $derived(dw.strokeBreakdown[0]?.style ?? "no data");
  let dominantPct = $derived(dw.strokeBreakdown[0]?.pctMeters ?? 0);

  // Total distance for THIS swim = sum of the donut's own slices, so it matches
  // the legend rows exactly. Fills the blank donut center: the swim's distance
  // is now stated, not left to hand-summing five stroke rows (persona gate
  // 2026-07-10, B2 — the "This swim" screen never named the day's distance).
  let totalMeters = $derived(
    dw.strokeBreakdown.reduce((sum, s) => sum + s.meters, 0),
  );
  let totalLabel = $derived(Math.round(totalMeters).toLocaleString());

  // Plain callout: "Freestyle was 69% of your swim." — sentence form, no comma read as list item.
  let callout = $derived(
    dw.strokeBreakdown.length > 0
      ? `${dominantStroke} was ${dominantPct.toFixed(0)}% of your swim.`
      : null,
  );
</script>

<div class="mix-layout">
  <div class="donut-wrap">
    <div class="donut" bind:this={donutEl}></div>
    <!-- B2: the swim's total distance, in the donut's own blank center -->
    <div class="donut-center">
      <span class="dc-value">{totalLabel}<span class="dc-unit">m</span></span>
      <span class="dc-label">this swim</span>
    </div>
  </div>
  <div class="right-col">
    {#if callout}
      <p class="callout">{callout}</p>
    {/if}
  </div>
</div>
<!-- Legend sits BELOW the donut row, full card width — not squeezed into the
     ~140px-narrower right-col alongside the donut. That column is only wide
     enough for the callout sentence; a name + meters + pct row for something
     like "Breaststroke" has no room left in it and was overflowing its own
     grid cell onto neighboring rows (persona-gate blocker). Full width gives
     every row comfortable space. -->
<div class="legend">
  {#each dw.strokeBreakdown as s, i (i)}
    {@const isDrill = ["Kick", "Kickboard", "Pull", "Drill", "Scull"].includes(s.style)}
    <div class="leg-row" class:leg-drill={isDrill}>
      <span class="dot" style:background={strokeColor(s.style)}></span>
      <span class="leg-name">{s.style}{#if isDrill}<span class="leg-tag">drill</span>{/if}</span>
      <span class="leg-meters">{Math.round(s.meters).toLocaleString()} m</span>
      <span class="leg-pct">{s.pctMeters.toFixed(0)}%</span>
    </div>
  {/each}
</div>

<style>
  .mix-layout {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 1rem;
    align-items: center;
  }

  .donut-wrap {
    position: relative;
    width: 140px;
    height: 140px;
  }
  .donut { width: 140px; height: 140px; }

  /* Total distance centered in the donut hole (inner radius 55% → ~77px clear).
     pointer-events:none so it never blocks the chart tooltip. */
  .donut-center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    pointer-events: none;
  }
  .dc-value {
    display: flex;
    align-items: baseline;
    gap: 2px;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary, #e4e2e4);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
    line-height: 1;
  }
  .dc-unit {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-body, #c5c6ce);
  }
  .dc-label {
    font-size: 11px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 2px;
  }

  .right-col {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    /* Grid items default to min-width: auto, which lets the min-content of
       the legend's longest unbroken string (e.g. a drill name + tag) force
       this column wider than the 1fr track and overflow the card. Force it
       to actually respect the track so the callout text wraps instead. */
    min-width: 0;
  }

  /* Dominant stroke plain callout — primary glance-read, 15px floor. */
  .callout {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary, #e4e2e4);
    margin: 0 0 0.25rem;
    line-height: 1.35;
  }

  .legend {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-top: 0.75rem;
  }

  /* Legend rows raised to 15px (from P5's ~12px). Name column is flexible
     (minmax(0,1fr)) so a long name never forces the row wider than the
     card; meters/pct are short fixed content and stay auto-sized. */
  .leg-row {
    display: grid;
    grid-template-columns: 12px minmax(0, 1fr) auto auto;
    gap: 0.4rem;
    align-items: center;
    font-variant-numeric: tabular-nums;
    font-size: 15px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  /* Ellipsize rather than overflow onto neighboring rows if a name ever
     doesn't fit its track (defense in depth — full-width layout above means
     this shouldn't trigger for any real stroke/drill name). */
  .leg-name {
    font-weight: 600;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .leg-meters { color: var(--text-muted); white-space: nowrap; }
  .leg-pct { font-weight: 600; }

  /* Padded chip, not bare text — margin (not a literal space in markup)
     guarantees separation from the name regardless of Svelte's whitespace
     trimming at {#if} block boundaries. */
  .leg-tag {
    display: inline-block;
    margin-left: 0.4rem;
    padding: 0.05rem 0.35rem;
    border-radius: 3px;
    background: var(--bg-canvas);
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
</style>
